/**
 * SONKUKI — predictive search + relevance ranking
 *
 * Shopify's default search ORs every token and matches vendor/body.
 * Almost every title starts with "SONKUKI", so a query like
 * "SONKUKI 7.5Ft LED Table Market Umbrella" returns pergolas/sofas first.
 * We strip the brand, search titles, then re-rank by title overlap.
 */
(function () {
  'use strict';

  var BRANDS = ['sonkuki', 'skk'];
  var STOP = ['a', 'an', 'the', 'and', 'or', 'for', 'with', 'of', 'to', 'in', 'on', 'by'];
  var CATEGORIES = [
    { keys: ['umbrella', 'umbrellas'], group: 'umbrella' },
    { keys: ['pergola', 'pergolas'], group: 'pergola' },
    { keys: ['sofa', 'sectional'], group: 'sofa' },
    { keys: ['chair', 'adirondack'], group: 'chair' },
    { keys: ['firepit', 'fire'], group: 'firepit' },
    { keys: ['base', 'bases'], group: 'base' },
    { keys: ['cover', 'covers'], group: 'cover' },
    { keys: ['table', 'tables'], group: 'table' }
  ];
  var PAGE_SIZE = 24;
  var SEARCH_FIELDS = 'title,product_type,variants.title,variants.sku';

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[“”"']/g, '')
      .replace(/×/g, 'x')
      .replace(/[^a-z0-9.x]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function stripBrand(value) {
    var text = normalize(value);
    BRANDS.forEach(function (brand) {
      text = text.replace(new RegExp('\\b' + brand + '\\b', 'g'), ' ');
    });
    return text.replace(/\s+/g, ' ').trim();
  }

  function tokenize(value) {
    return normalize(value).split(' ').filter(function (token) {
      return token && STOP.indexOf(token) === -1 && BRANDS.indexOf(token) === -1;
    });
  }

  function findCategory(text) {
    var normalized = normalize(text);
    for (var i = 0; i < CATEGORIES.length; i++) {
      for (var j = 0; j < CATEGORIES[i].keys.length; j++) {
        if (normalized.indexOf(CATEGORIES[i].keys[j]) !== -1) {
          return CATEGORIES[i].group;
        }
      }
    }
    return '';
  }

  function unique(items) {
    var seen = {};
    return items.filter(function (item) {
      if (!item || seen[item]) return false;
      seen[item] = true;
      return true;
    });
  }

  function searchQueries(raw) {
    var cleaned = stripBrand(raw);
    var tokens = tokenize(raw);
    var category = findCategory(cleaned);
    var queries = [];

    if (cleaned) queries.push(cleaned);
    if (category) {
      var size = (cleaned.match(/\d+(?:\.\d+)?(?:\s*x\s*\d+(?:\.\d+)?)?(?:ft)?/) || [])[0];
      if (size) queries.push((size + ' ' + category).replace(/\s+/g, ' ').trim());
      var distinctive = tokens.filter(function (token) {
        return token === category || token === 'led' || token === 'market' || token === 'cantilever' || /\d/.test(token);
      });
      if (distinctive.length) queries.push(distinctive.join(' '));
    }

    return unique(queries);
  }

  function scoreProduct(product, query) {
    var title = product.title || '';
    var type = product.type || '';
    var titleN = normalize(title);
    var typeN = normalize(type);
    var queryN = normalize(query);
    var cleaned = stripBrand(query);
    var tokens = tokenize(query);
    var score = 0;

    if (!tokens.length) return 0;

    if (titleN === queryN || (cleaned && titleN === cleaned)) score += 1000;
    if (cleaned && titleN.indexOf(cleaned) !== -1) score += 420;
    if (queryN && titleN.indexOf(queryN) !== -1) score += 180;

    var hits = 0;
    tokens.forEach(function (token) {
      if (titleN.indexOf(token) !== -1) {
        hits += 1;
        score += 50;
      } else if (typeN.indexOf(token) !== -1) {
        score += 8;
      }
    });
    if (hits === tokens.length) score += 240;
    score += (hits / tokens.length) * 90;

    var queryCategory = findCategory(cleaned);
    var productCategory = findCategory(titleN + ' ' + typeN);
    if (queryCategory) {
      score += productCategory === queryCategory ? 200 : -140;
    }

    var sizes = cleaned.match(/\d+(?:\.\d+)?(?:ft)?/g) || [];
    sizes.forEach(function (size) {
      if (titleN.indexOf(size) !== -1) score += 90;
    });

    return score;
  }

  function rankProducts(products, query) {
    var queryCategory = findCategory(stripBrand(query));
    var scored = (products || []).map(function (product) {
      return {
        product: product,
        score: scoreProduct(product, query)
      };
    });

    if (queryCategory) {
      var matched = scored.filter(function (entry) {
        return findCategory((entry.product.title || '') + ' ' + (entry.product.type || '')) === queryCategory;
      });
      if (matched.length) scored = matched;
    }

    scored.sort(function (a, b) {
      return b.score - a.score;
    });

    if (scored.length && scored[0].score >= 200) {
      scored = scored.filter(function (entry) {
        return entry.score >= 80;
      });
    }

    return scored.map(function (entry) {
      return entry.product;
    });
  }

  function mergeProducts(lists) {
    var seen = {};
    var merged = [];
    lists.forEach(function (list) {
      (list || []).forEach(function (product) {
        var key = String(product.id || product.handle || product.url || product.title);
        if (seen[key]) return;
        seen[key] = true;
        merged.push(product);
      });
    });
    return merged;
  }

  function formatMoney(cents) {
    var amount = Math.round(Number(cents));
    if (isNaN(amount)) return '';
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(amount);
    }
    return '$' + (amount / 100).toFixed(2);
  }

  function formatSuggestPrice(value) {
    if (value == null || value === '') return '';
    var amount = parseFloat(String(value));
    if (isNaN(amount)) return '';
    return formatMoney(Math.round(amount * 100));
  }

  function parsePrice(product) {
    if (product.price_min != null && product.price_min !== '') {
      return formatSuggestPrice(product.price_min);
    }
    if (product.price != null && product.price !== '') {
      return formatSuggestPrice(product.price);
    }
    return '';
  }

  function parseCompare(product) {
    var compare = product.compare_at_price_max || product.compare_at_price_min;
    if (compare == null || compare === '') return '';

    var compareAmount = parseFloat(String(compare));
    var priceAmount = parseFloat(String(product.price_min || product.price || 0));
    if (isNaN(compareAmount) || isNaN(priceAmount)) return '';
    if (compareAmount > priceAmount) return formatSuggestPrice(compare);
    return '';
  }

  function getImageUrl(product) {
    if (product.featured_image && product.featured_image.url) return product.featured_image.url;
    if (product.image) return product.image;
    if (product.html) {
      var match = String(product.html).match(/src="([^"]+)"/);
      if (match) return match[1];
    }
    return '';
  }

  function suggestUrl(query) {
    return (
      '/search/suggest.json?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product' +
      '&resources[limit]=10' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=' +
      encodeURIComponent(SEARCH_FIELDS)
    );
  }

  function fetchSuggest(query, signal) {
    return fetch(suggestUrl(query), { signal: signal }).then(function (res) {
      return res.json();
    }).then(function (data) {
      return (data.resources && data.resources.results && data.resources.results.products) || [];
    });
  }

  function parseAjaxPayload(text) {
    var trimmed = String(text || '').trim();
    var start = trimmed.indexOf('{');
    var end = trimmed.lastIndexOf('}');
    if (start === -1 || end === -1) throw new Error('Invalid search payload');
    return JSON.parse(trimmed.slice(start, end + 1));
  }

  function fetchAjaxProducts(query, signal) {
    var url =
      '/search?q=' +
      encodeURIComponent(query) +
      '&type=product&view=ajax&options[unavailable_products]=last';
    return fetch(url, { signal: signal }).then(function (res) {
      return res.text();
    }).then(parseAjaxPayload).then(function (data) {
      return data.products || [];
    });
  }

  /* ---------- Predictive modal ---------- */
  var modal = document.querySelector('[data-search-modal]');
  var panel = modal && modal.querySelector('.search-modal__panel');
  var form = modal && modal.querySelector('[data-search-form]');
  var input = modal && modal.querySelector('[data-search-input]');
  var clearBtn = modal && modal.querySelector('[data-search-clear]');
  var loading = modal && modal.querySelector('[data-search-loading]');
  var defaultPanel = modal && modal.querySelector('[data-search-default]');
  var resultsWrap = modal && modal.querySelector('[data-search-results-wrap]');
  var resultsList = modal && modal.querySelector('[data-search-results]');
  var resultsLabel = modal && modal.querySelector('[data-search-results-label]');
  var emptyPanel = modal && modal.querySelector('[data-search-empty]');
  var viewAll = modal && modal.querySelector('[data-search-view-all]');
  var debounceTimer;
  var lastQuery = '';
  var abortController;

  function open() {
    if (!modal) return;
    modal.hidden = false;
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    if (panel) panel.focus();
    if (input) {
      input.focus();
      if (input.value.trim()) fetchSuggestions(input.value.trim());
    }
  }

  function close() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.hidden = true;
    document.body.style.overflow = '';
    if (abortController) abortController.abort();
  }

  function setLoading(on) {
    if (loading) loading.hidden = !on;
  }

  function showDefault() {
    if (defaultPanel) defaultPanel.hidden = false;
    if (resultsWrap) resultsWrap.hidden = true;
    if (emptyPanel) emptyPanel.hidden = true;
  }

  function showResults(count) {
    if (defaultPanel) defaultPanel.hidden = true;
    if (resultsWrap) resultsWrap.hidden = false;
    if (emptyPanel) emptyPanel.hidden = true;
    if (resultsLabel) {
      resultsLabel.textContent = count === 1 ? '1 product' : count + ' products';
    }
  }

  function showEmpty() {
    if (defaultPanel) defaultPanel.hidden = true;
    if (resultsWrap) resultsWrap.hidden = true;
    if (emptyPanel) emptyPanel.hidden = false;
  }

  function renderProducts(products, query) {
    if (!resultsList) return;
    resultsList.innerHTML = '';

    products.forEach(function (product) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = product.url;
      a.className = 'search-modal__result';
      a.setAttribute('data-search-result', '');

      var imgWrap = document.createElement('span');
      imgWrap.className = 'search-modal__result-media';
      var imgUrl = getImageUrl(product);
      if (imgUrl) {
        var img = document.createElement('img');
        img.src = imgUrl;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 80;
        img.height = 80;
        imgWrap.appendChild(img);
      }

      var body = document.createElement('span');
      body.className = 'search-modal__result-body';
      var title = document.createElement('span');
      title.className = 'search-modal__result-title';
      title.textContent = product.title;

      var priceRow = document.createElement('span');
      priceRow.className = 'search-modal__result-prices';
      var price = parsePrice(product);
      var compare = parseCompare(product);
      if (price) {
        var priceEl = document.createElement('span');
        priceEl.className = 'search-modal__result-price';
        priceEl.textContent = price;
        priceRow.appendChild(priceEl);
      }
      if (compare) {
        var compareEl = document.createElement('span');
        compareEl.className = 'search-modal__result-compare';
        compareEl.textContent = compare;
        priceRow.appendChild(compareEl);
      }

      body.appendChild(title);
      if (priceRow.childNodes.length) body.appendChild(priceRow);

      a.appendChild(imgWrap);
      a.appendChild(body);
      li.appendChild(a);
      resultsList.appendChild(li);
    });

    if (viewAll && query) {
      viewAll.href = '/search?q=' + encodeURIComponent(query) + '&type=product';
    }
  }

  function fetchSuggestions(query) {
    if (!modal) return;
    if (query.length < 2) {
      showDefault();
      if (clearBtn) clearBtn.hidden = true;
      return;
    }

    if (clearBtn) clearBtn.hidden = false;
    if (query === lastQuery) return;
    lastQuery = query;

    if (abortController) abortController.abort();
    abortController = new AbortController();
    var signal = abortController.signal;

    setLoading(true);

    var queries = searchQueries(query);
    if (!queries.length) queries = [query];

    var jobs = queries.slice(0, 2).map(function (q) {
      return fetchSuggest(q, signal).catch(function () {
        return [];
      });
    });
    jobs.push(
      fetchAjaxProducts(queries[0], signal).catch(function () {
        return [];
      })
    );

    Promise.all(jobs)
      .then(function (lists) {
        var products = rankProducts(mergeProducts(lists), query).slice(0, 8);
        if (products.length === 0) {
          showEmpty();
        } else {
          showResults(products.length);
          renderProducts(products, query);
        }
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        showEmpty();
      })
      .finally(function () {
        setLoading(false);
      });
  }

  if (modal) {
    document.querySelectorAll('[data-search-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });

    modal.querySelectorAll('[data-search-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    if (input) {
      input.addEventListener('input', function () {
        var q = input.value.trim();
        window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(function () {
          fetchSuggestions(q);
        }, 280);
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
      });
    }

    if (clearBtn && input) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        lastQuery = '';
        clearBtn.hidden = true;
        input.focus();
        showDefault();
      });
    }

    modal.querySelectorAll('[data-search-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var term = chip.getAttribute('data-search-chip') || chip.textContent;
        if (input) {
          input.value = term.trim();
          lastQuery = '';
          fetchSuggestions(term.trim());
          input.focus();
        }
      });
    });

    if (form) {
      form.addEventListener('submit', function () {
        close();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });
  }

  /* ---------- Search page ranking ---------- */
  var pageInput = document.querySelector('[data-search-page-input]');
  var pageClear = document.querySelector('[data-search-page-clear]');
  if (pageInput) {
    pageInput.focus();
    if (pageClear) {
      pageClear.hidden = !pageInput.value.trim();
      pageInput.addEventListener('input', function () {
        pageClear.hidden = !pageInput.value.trim();
      });
      pageClear.addEventListener('click', function () {
        pageInput.value = '';
        pageClear.hidden = true;
        pageInput.focus();
      });
    }
  }

  function countMarkup(count, page, pages) {
    if (!count) return 'No products found';
    var html = '<strong>' + count + '</strong> product' + (count === 1 ? '' : 's');
    if (pages > 1) {
      html +=
        ' <span class="search-page__count-page">&middot; Page ' +
        page +
        ' of ' +
        pages +
        '</span>';
    }
    return html;
  }

  function renderPagePagination(nav, page, pages) {
    if (!nav) return;
    if (pages <= 1) {
      nav.hidden = true;
      nav.innerHTML = '';
      return;
    }

    nav.hidden = false;
    var prev =
      page > 1
        ? '<button type="button" class="btn btn--ghost btn--md search-page__pagination-btn" data-search-page-btn="' +
          (page - 1) +
          '">Previous</button>'
        : '';
    var next =
      page < pages
        ? '<button type="button" class="btn btn--ghost btn--md search-page__pagination-btn" data-search-page-btn="' +
          (page + 1) +
          '">Next</button>'
        : '';
    nav.innerHTML =
      prev +
      '<span class="search-page__pagination-info">Page ' +
      page +
      ' of ' +
      pages +
      '</span>' +
      next;
  }

  function productsFromDom(grid) {
    return Array.prototype.map.call(grid.querySelectorAll('[data-search-item]'), function (el) {
      return {
        title: el.getAttribute('data-search-title') || '',
        type: el.getAttribute('data-search-type') || '',
        html: el.outerHTML
      };
    });
  }

  function enhanceSearchPage() {
    var pageRoot = document.querySelector('[data-search-page]');
    var grid = document.querySelector('[data-search-grid]');
    if (!pageRoot || !grid) return;

    var terms = pageRoot.getAttribute('data-search-terms') || '';
    if (!terms.trim()) return;

    var countEl = document.querySelector('[data-search-count]');
    var nav = document.querySelector('[data-search-pagination]');
    if (!nav) {
      nav = document.createElement('nav');
      nav.className = 'search-page__pagination';
      nav.setAttribute('aria-label', 'Search pagination');
      nav.setAttribute('data-search-pagination', '');
      grid.insertAdjacentElement('afterend', nav);
    }

    var state = {
      products: [],
      page: 1
    };

    function paint() {
      var pages = Math.max(1, Math.ceil(state.products.length / PAGE_SIZE));
      if (state.page > pages) state.page = pages;
      var start = (state.page - 1) * PAGE_SIZE;
      var slice = state.products.slice(start, start + PAGE_SIZE);
      grid.innerHTML = slice
        .map(function (product) {
          if (product.html && product.html.indexOf('data-search-item') !== -1) {
            return product.html;
          }
          return (
            '<div class="search-page__grid-item" data-search-item data-search-title="' +
            String(product.title || '').replace(/"/g, '&quot;') +
            '">' +
            (product.html || '') +
            '</div>'
          );
        })
        .join('');
      if (countEl) countEl.innerHTML = countMarkup(state.products.length, state.page, pages);
      renderPagePagination(nav, state.page, pages);
    }

    nav.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-search-page-btn]');
      if (!btn) return;
      e.preventDefault();
      state.page = parseInt(btn.getAttribute('data-search-page-btn'), 10) || 1;
      paint();
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    var queries = searchQueries(terms);
    if (!queries.length) queries = [terms];

    Promise.all(
      queries.slice(0, 2).map(function (q) {
        return fetchAjaxProducts(q).catch(function () {
          return [];
        });
      })
    )
      .then(function (lists) {
        var merged = mergeProducts(lists);
        if (!merged.length) merged = productsFromDom(grid);
        state.products = rankProducts(merged, terms);
        state.page = 1;
        paint();
      })
      .catch(function () {
        state.products = rankProducts(productsFromDom(grid), terms);
        paint();
      });
  }

  enhanceSearchPage();

  window.SonkukiSearch = { open: open, close: close };
})();
